import { useEffect, useRef, type MutableRefObject, type RefObject } from "react";
import maplibregl, { Map as MapLibreMap, Popup } from "maplibre-gl";
import type { Vehicle } from "./useVehicleStream";
import type { HistoryStore } from "./positionHistory";

// OpenFreeMap is a free, no-key OSM-derived vector tile service. Replace with
// MapTiler / Stadia / Protomaps / self-hosted PMTiles before going to
// production if you want stability guarantees.
const STYLE_URL_LIGHT = "https://tiles.openfreemap.org/styles/positron";
const STYLE_URL_DARK = "https://tiles.openfreemap.org/styles/dark-matter";

function styleUrlFor(theme: "light" | "dark"): string {
  return theme === "dark" ? STYLE_URL_DARK : STYLE_URL_LIGHT;
}

// Theme-dependent paint values for our overlay layers (stop / vehicle labels)
// so they stay legible against the basemap.
const LABEL_PAINT = {
  light: {
    stopText: "#333",
    stopHalo: "#ffffff",
    vehicleText: "#222",
    vehicleHalo: "#ffffff",
    stopStroke: "#222",
  },
  dark: {
    stopText: "#e5e5ea",
    stopHalo: "#0b0d12",
    vehicleText: "#e5e5ea",
    vehicleHalo: "#0b0d12",
    stopStroke: "#0b0d12",
  },
} as const;

// Default view: downtown Seattle, framed to comfortably show the Link 1 Line
// from SeaTac to Northgate.
const DEFAULT_CENTER: [number, number] = [-122.33, 47.62];
const DEFAULT_ZOOM = 11;

// Fallback color used when the upstream feed didn't publish a route color.
const DEFAULT_COLOR = "#1f78ff";

// Zoom level used when we successfully snap to the rider's nearest stop.
const NEAREST_STOP_ZOOM = 15;
// Ignore geolocation fixes that aren't reasonably close to any stop in the
// dataset — riders pulling up the map from across the country shouldn't get
// yanked off to a random stop. Roughly 50 km, which comfortably covers the
// service area plus suburbs.
const NEAREST_STOP_MAX_KM = 50;

// How many historical samples to draw as a ghost trail behind each vehicle
// (excluding the current position, which the vehicle marker already shows).
const TRAIL_SAMPLES = 5;
// Only show trail points from the last N seconds to avoid stale ghosts.
const TRAIL_MAX_AGE_SEC = 10 * 60;

export function TransitMap({
  vehicles,
  fitTrigger,
  history,
  historyVersion,
  theme,
}: {
  vehicles: Vehicle[];
  fitTrigger?: number;
  history?: HistoryStore;
  historyVersion?: number;
  theme: "light" | "dark";
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const popupRef = useRef<Popup | null>(null);
  const selectedSrcRef = useRef<maplibregl.GeoJSONSource | null>(null);
  // Latest theme, mirrored so callbacks fired after a style swap can read the
  // current value (refs are stable across re-renders).
  const themeRef = useRef<"light" | "dark">(theme);
  themeRef.current = theme;
  // Cached static route data from /api/routes/shapes, fetched once and
  // re-applied after every style change.
  const shapesRef = useRef<{
    tracks?: GeoJSON.FeatureCollection;
    stops?: GeoJSON.FeatureCollection;
  }>({});
  // Latest live data, re-applied after every style change.
  const vehiclesRef = useRef<Vehicle[]>(vehicles);
  vehiclesRef.current = vehicles;
  const trailFeaturesRef = useRef<GeoJSON.FeatureCollection>({
    type: "FeatureCollection",
    features: [],
  });
  // Tracks whether we've already attached click handlers (those bind to layer
  // ids and are map-instance scoped, so they survive style swaps and we only
  // want them once).
  const handlersAttachedRef = useRef(false);

  // Create the map once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: styleUrlFor(themeRef.current),
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      attributionControl: { compact: true },
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }));
    mapRef.current = map;

    // `style.load` fires after the initial style load AND after any
    // setStyle() call, so we use it as the single setup point. Custom
    // sources/layers are wiped by setStyle, so we re-add them every time
    // and re-apply the cached data we already have.
    map.on("style.load", () => {
      installCustomLayers(map, themeRef.current);
      reapplyCachedData(map, shapesRef, vehiclesRef, trailFeaturesRef);
      if (!handlersAttachedRef.current) {
        attachInteractionHandlers(map, popupRef, selectedSrcRef);
        handlersAttachedRef.current = true;
        // First-time setup: fetch route geometry and try to recenter.
        void loadRouteShapesAndCenter(map, shapesRef);
      } else {
        // After a style swap, re-grab the fresh source reference for the
        // selection ring (the old one is stale).
        selectedSrcRef.current = map.getSource(
          "vehicles-selected",
        ) as maplibregl.GeoJSONSource;
      }
    });

    return () => {
      popupRef.current?.remove();
      popupRef.current = null;
      map.remove();
      mapRef.current = null;
      handlersAttachedRef.current = false;
    };
  }, []);

  // Swap basemap when the theme prop changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.setStyle(styleUrlFor(theme));
  }, [theme]);

  // Fit the map to the bounding box of all vehicles when fitTrigger changes.
  useEffect(() => {
    if (!fitTrigger || vehicles.length === 0) return;
    const map = mapRef.current;
    if (!map) return;
    const lons = vehicles.map((v) => v.lon);
    const lats = vehicles.map((v) => v.lat);
    map.fitBounds(
      [
        [Math.min(...lons), Math.min(...lats)],
        [Math.max(...lons), Math.max(...lats)],
      ],
      { padding: 60, maxZoom: 14, duration: 800 },
    );
  }, [fitTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update ghost trail dots when position history advances.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !history) return;
    const nowSec = Math.floor(Date.now() / 1000);
    const cutoff = nowSec - TRAIL_MAX_AGE_SEC;

    const features: GeoJSON.Feature[] = [];
    for (const [, vh] of history.byVehicle) {
      const color = vh.routeColor ? `#${vh.routeColor}` : DEFAULT_COLOR;
      // Exclude the most-recent sample (already shown as the vehicle marker).
      const trail = vh.samples
        .slice(0, -1)
        .filter((s) => s.ts >= cutoff)
        .slice(-TRAIL_SAMPLES);
      const n = trail.length;
      trail.forEach((s, i) => {
        // Oldest samples are faintest; newest trail point is most opaque.
        const alpha = 0.12 + 0.28 * ((i + 1) / n);
        features.push({
          type: "Feature",
          geometry: { type: "Point", coordinates: [s.lon, s.lat] },
          properties: { color, alpha },
        });
      });
    }

    const fc: GeoJSON.FeatureCollection = {
      type: "FeatureCollection",
      features,
    };
    trailFeaturesRef.current = fc;
    const apply = () => {
      const src = map.getSource("vehicle-trails") as
        | maplibregl.GeoJSONSource
        | undefined;
      if (src) src.setData(fc);
    };
    if (map.isStyleLoaded()) apply();
    else map.once("style.load", apply);
  }, [historyVersion, history]);

  // Push vehicle updates into the geojson source.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = () => {
      const source = map.getSource("vehicles") as
        | maplibregl.GeoJSONSource
        | undefined;
      if (!source) return;
      source.setData({
        type: "FeatureCollection",
        features: vehicles.map((v) => ({
          type: "Feature",
          geometry: { type: "Point", coordinates: [v.lon, v.lat] },
          properties: {
            id: v.vehicle_id,
            trip: v.trip_id || "",
            route: v.route_short_name || v.route_id || "",
            label: v.route_short_name || "",
            headsign: v.trip_headsign || "",
            color: v.route_color ? `#${v.route_color}` : DEFAULT_COLOR,
            bearing: v.bearing ?? 0,
            ts: v.ts,
          },
        })),
      });
    };
    if (map.isStyleLoaded()) apply();
    else map.once("style.load", apply);
  }, [vehicles]);

  return <div ref={containerRef} className="map" />;
}

// installCustomLayers (re-)adds all of our custom sources and layers to a
// freshly-loaded style. Called on every `style.load` event so the overlays
// survive theme-driven setStyle() calls. Source data is restored separately
// by reapplyCachedData().
function installCustomLayers(map: MapLibreMap, theme: "light" | "dark"): void {
  const paint = LABEL_PAINT[theme];

  // Static route geometry (track polylines + stops). Pre-fetched server-side
  // and served from /api/routes/shapes; we just drop it into two GeoJSON
  // sources. Tracks render below stops below vehicles.
  map.addSource("tracks", {
    type: "geojson",
    data: { type: "FeatureCollection", features: [] },
  });
  map.addSource("stops", {
    type: "geojson",
    data: { type: "FeatureCollection", features: [] },
  });

  // Soft white casing under the route line so it stays legible over dark map
  // elements (water, parks).
  // Per-route perpendicular offset so the 1 Line and 2 Line don't paint on
  // top of each other through the shared downtown trunk. The Link shapes run
  // roughly north-to-south, so a positive `line-offset` (right of the line
  // direction) ends up roughly east, and negative ends up roughly west. We
  // push 1 Line west and 2 Line east.
  const routeOffset: maplibregl.ExpressionSpecification = [
    "case",
    ["==", ["get", "route_short_name"], "1 Line"],
    -5,
    ["==", ["get", "route_short_name"], "2 Line"],
    5,
    0,
  ];
  map.addLayer({
    id: "tracks-casing",
    type: "line",
    source: "tracks",
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": theme === "dark" ? "#0b0d12" : "#ffffff",
      "line-opacity": 0.65,
      "line-width": [
        "interpolate",
        ["linear"],
        ["zoom"],
        8,
        3,
        12,
        6,
        16,
        10,
      ],
      "line-offset": routeOffset,
    },
  });
  map.addLayer({
    id: "tracks",
    type: "line",
    source: "tracks",
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": ["coalesce", ["get", "color"], DEFAULT_COLOR],
      "line-opacity": 0.85,
      "line-width": [
        "interpolate",
        ["linear"],
        ["zoom"],
        8,
        1.5,
        12,
        3,
        16,
        5,
      ],
      "line-offset": routeOffset,
    },
  });

  // Stops: small filled dot with a halo. Names appear once you zoom in past
  // 12 so they don't clutter the regional view.
  map.addLayer({
    id: "stops-dot",
    type: "circle",
    source: "stops",
    paint: {
      "circle-radius": [
        "interpolate",
        ["linear"],
        ["zoom"],
        10,
        2.5,
        14,
        4.5,
        16,
        6,
      ],
      "circle-color": "#ffffff",
      "circle-stroke-color": paint.stopStroke,
      "circle-stroke-width": 1.5,
    },
  });
  map.addLayer({
    id: "stops-label",
    type: "symbol",
    source: "stops",
    minzoom: 12,
    layout: {
      "text-field": ["get", "name"],
      // OpenFreeMap only serves the Noto Sans family; the maplibre default
      // ("Open Sans Regular") 404s, which causes the worker to fail tile
      // assembly and the whole tile (including non-symbol layers) errors.
      "text-font": ["Noto Sans Regular"],
      "text-size": 11,
      "text-offset": [0, 1],
      "text-anchor": "top",
      "text-allow-overlap": false,
      "text-optional": true,
    },
    paint: {
      "text-color": paint.stopText,
      "text-halo-color": paint.stopHalo,
      "text-halo-width": 1.5,
    },
  });

  // Ghost trail dots rendered behind vehicle markers.
  map.addSource("vehicle-trails", {
    type: "geojson",
    data: { type: "FeatureCollection", features: [] },
  });
  map.addLayer({
    id: "vehicle-trails",
    type: "circle",
    source: "vehicle-trails",
    paint: {
      "circle-radius": 3,
      "circle-color": ["get", "color"],
      "circle-opacity": ["get", "alpha"],
    },
  });

  map.addSource("vehicles", {
    type: "geojson",
    data: { type: "FeatureCollection", features: [] },
  });

  // Outer halo so vehicles stay visible against the route color.
  // Pulsing accent ring drawn beneath the halo for the selected vehicle.
  map.addSource("vehicles-selected", {
    type: "geojson",
    data: { type: "FeatureCollection", features: [] },
  });
  map.addLayer({
    id: "vehicles-selected-ring",
    type: "circle",
    source: "vehicles-selected",
    paint: {
      "circle-radius": 14,
      "circle-color": "transparent",
      "circle-stroke-color": "#ff8c00",
      "circle-stroke-width": 3,
    },
  });

  map.addLayer({
    id: "vehicles-halo",
    type: "circle",
    source: "vehicles",
    paint: {
      "circle-radius": 9,
      "circle-color": "#ffffff",
      "circle-stroke-color": ["get", "color"],
      "circle-stroke-width": 2,
    },
  });
  // Directional arrow in the route color, rotated to match the train bearing
  // while staying inside the existing circle indicator.
  map.addLayer({
    id: "vehicles-dot",
    type: "symbol",
    source: "vehicles",
    layout: {
      "text-field": "↑",
      "text-font": ["Noto Sans Regular"],
      "text-size": 14,
      "text-anchor": "center",
      "text-allow-overlap": true,
      "text-ignore-placement": true,
      "text-rotate": ["get", "bearing"],
      "text-rotation-alignment": "map",
    },
    paint: {
      "text-color": ["get", "color"],
    },
  });
  // Route label drawn next to each vehicle.
  map.addLayer({
    id: "vehicles-label",
    type: "symbol",
    source: "vehicles",
    layout: {
      "text-field": ["get", "label"],
      // See note on stops-label — must use a font OpenFreeMap serves.
      "text-font": ["Noto Sans Regular"],
      "text-size": 11,
      "text-offset": [0.9, 0],
      "text-anchor": "left",
      "text-allow-overlap": true,
    },
    paint: {
      "text-color": paint.vehicleText,
      "text-halo-color": paint.vehicleHalo,
      "text-halo-width": 1.5,
    },
  });
}

// reapplyCachedData restores the most-recent source data onto a freshly-built
// style. setStyle() wipes our custom sources, so we feed back what we have.
function reapplyCachedData(
  map: MapLibreMap,
  shapesRef: RefObject<{
    tracks?: GeoJSON.FeatureCollection;
    stops?: GeoJSON.FeatureCollection;
  }>,
  vehiclesRef: RefObject<Vehicle[]>,
  trailFeaturesRef: RefObject<GeoJSON.FeatureCollection>,
): void {
  const tracksSrc = map.getSource("tracks") as
    | maplibregl.GeoJSONSource
    | undefined;
  const stopsSrc = map.getSource("stops") as
    | maplibregl.GeoJSONSource
    | undefined;
  if (tracksSrc && shapesRef.current?.tracks)
    tracksSrc.setData(shapesRef.current.tracks);
  if (stopsSrc && shapesRef.current?.stops)
    stopsSrc.setData(shapesRef.current.stops);

  const vehSrc = map.getSource("vehicles") as
    | maplibregl.GeoJSONSource
    | undefined;
  if (vehSrc && vehiclesRef.current) {
    vehSrc.setData({
      type: "FeatureCollection",
      features: vehiclesRef.current.map((v) => ({
        type: "Feature",
        geometry: { type: "Point", coordinates: [v.lon, v.lat] },
        properties: {
          id: v.vehicle_id,
          trip: v.trip_id || "",
          route: v.route_short_name || v.route_id || "",
          label: v.route_short_name || "",
          headsign: v.trip_headsign || "",
          color: v.route_color ? `#${v.route_color}` : DEFAULT_COLOR,
          bearing: v.bearing ?? 0,
          ts: v.ts,
        },
      })),
    });
  }

  const trailSrc = map.getSource("vehicle-trails") as
    | maplibregl.GeoJSONSource
    | undefined;
  if (trailSrc && trailFeaturesRef.current)
    trailSrc.setData(trailFeaturesRef.current);
}

// attachInteractionHandlers wires up click + hover handlers for the vehicle
// and stop layers. These are layer-id scoped and stored on the map instance,
// so they survive setStyle() calls and only need to be installed once.
function attachInteractionHandlers(
  map: MapLibreMap,
  popupRef: MutableRefObject<Popup | null>,
  selectedSrcRef: MutableRefObject<maplibregl.GeoJSONSource | null>,
): void {
  // Click a vehicle to see route + headsign + last update.
  map.on("click", "vehicles-halo", (e) => {
    const f = e.features?.[0];
    if (!f || f.geometry.type !== "Point") return;
    const [lon, lat] = f.geometry.coordinates as [number, number];
    const props = f.properties as Record<string, string>;
    const ageSec = Math.max(
      0,
      Math.round(Date.now() / 1000 - Number(props.ts || 0)),
    );
    // Place accent ring at the clicked vehicle's position.
    const sel = (selectedSrcRef.current = map.getSource(
      "vehicles-selected",
    ) as maplibregl.GeoJSONSource);
    sel?.setData({
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          geometry: { type: "Point", coordinates: [lon, lat] },
          properties: {},
        },
      ],
    });
    popupRef.current?.remove();
    popupRef.current = new maplibregl.Popup({ offset: 12 })
      .setLngLat([lon, lat])
      .setHTML(
        `<div style="font:13px -apple-system,Segoe UI,Roboto,sans-serif">
           <div style="font-weight:600">${escapeHtml(props.route || "")}${props.headsign ? ` &rarr; ${escapeHtml(props.headsign)}` : ""}</div>
           <div style="color:#555">vehicle ${escapeHtml(props.id || "")}</div>
           ${props.trip ? `<div style="color:#555">trip ${escapeHtml(shortTripID(props.trip))}</div>` : ""}
           <div style="color:#555">updated ${ageSec}s ago</div>
         </div>`,
      )
      .addTo(map);
    // Clear the ring when the popup is dismissed.
    popupRef.current.once("close", () => {
      const sel2 = map.getSource("vehicles-selected") as
        | maplibregl.GeoJSONSource
        | undefined;
      sel2?.setData({
        type: "FeatureCollection",
        features: [],
      });
    });
  });
  map.on("mouseenter", "vehicles-halo", () => {
    map.getCanvas().style.cursor = "pointer";
  });
  map.on("mouseleave", "vehicles-halo", () => {
    map.getCanvas().style.cursor = "";
  });

  // Click a stop to see its name + code.
  map.on("click", "stops-dot", (e) => {
    const f = e.features?.[0];
    if (!f || f.geometry.type !== "Point") return;
    const [lon, lat] = f.geometry.coordinates as [number, number];
    const props = f.properties as Record<string, string>;
    popupRef.current?.remove();
    popupRef.current = new maplibregl.Popup({ offset: 8 })
      .setLngLat([lon, lat])
      .setHTML(
        `<div style="font:13px -apple-system,Segoe UI,Roboto,sans-serif">
           <div style="font-weight:600">${escapeHtml(props.name || "")}</div>
           ${props.code ? `<div style="color:#555">stop ${escapeHtml(props.code)}</div>` : ""}
         </div>`,
      )
      .addTo(map);
  });
  map.on("mouseenter", "stops-dot", () => {
    map.getCanvas().style.cursor = "pointer";
  });
  map.on("mouseleave", "stops-dot", () => {
    map.getCanvas().style.cursor = "";
  });
}

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[c]!,
  );
}

// shortTripID turns OBA's verbose trip identifier into something a human
// can scan at a glance. Real Link trip ids look like:
//
//   40_LLR_2026-05-04_20260409_Link_PinehurstMar26NB_Weekday_100479_2076
//   40_LLR_2026-05-04_20260409_Link_PinehurstMar26NB_Weekday_2LINE_4080
//
// The trailing token is the run number (the only piece that actually
// varies between trips); we strip the optional `.NNNNN` sub-trip suffix
// some entries carry. Direction comes from the trip headsign, so we
// don't try to extract it from the id (the "NB" you see in
// `PinehurstMar26NB` is the schedule version label, not the trip
// direction). Falls back to the raw id when the format doesn't match.
function shortTripID(s: string): string {
  const parts = s.split("_");
  if (parts.length < 2) return s;
  const tail = parts[parts.length - 1];
  const m = tail.match(/^(\d+)/);
  return m ? m[1] : tail;
}

// loadRouteShapesAndCenter hits the backend's pre-rendered shapes endpoint and
// feeds the GeoJSON into the `tracks` and `stops` sources. Once stops are in
// place it asks the browser for the rider's location and, if the user hasn't
// already started interacting with the map, flies to the nearest stop.
// Failures are swallowed — the map still works without the static overlay or
// without geolocation permission.
async function loadRouteShapesAndCenter(
  map: MapLibreMap,
  shapesRef: MutableRefObject<{
    tracks?: GeoJSON.FeatureCollection;
    stops?: GeoJSON.FeatureCollection;
  }>,
): Promise<void> {
  let stops: GeoJSON.FeatureCollection | undefined;
  try {
    const res = await fetch("/api/routes/shapes");
    if (!res.ok) return;
    const data = (await res.json()) as {
      tracks?: GeoJSON.FeatureCollection;
      stops?: GeoJSON.FeatureCollection;
    };
    // Cache for re-application after style swaps (theme changes).
    shapesRef.current = { tracks: data.tracks, stops: data.stops };
    const tracksSrc = map.getSource("tracks") as
      | maplibregl.GeoJSONSource
      | undefined;
    const stopsSrc = map.getSource("stops") as
      | maplibregl.GeoJSONSource
      | undefined;
    if (data.tracks && tracksSrc) tracksSrc.setData(data.tracks);
    if (data.stops && stopsSrc) stopsSrc.setData(data.stops);
    stops = data.stops;
  } catch {
    // Network error or invalid JSON — leave the map without static overlays.
  }

  if (!stops || !stops.features?.length) return;
  if (typeof navigator === "undefined" || !navigator.geolocation) return;

  // If the rider grabs the map before the geolocation prompt resolves, skip
  // the auto-snap so we don't yank them away from where they're looking.
  let userMoved = false;
  const markMoved = (e: { originalEvent?: unknown }) => {
    if (e.originalEvent) userMoved = true;
  };
  map.on("dragstart", markMoved);
  map.on("zoomstart", markMoved);
  const detach = () => {
    map.off("dragstart", markMoved);
    map.off("zoomstart", markMoved);
  };

  navigator.geolocation.getCurrentPosition(
    (pos) => {
      detach();
      if (userMoved) return;
      const nearest = nearestStop(stops!, pos.coords.longitude, pos.coords.latitude);
      if (!nearest) return;
      if (nearest.distanceKm > NEAREST_STOP_MAX_KM) return;
      map.flyTo({
        center: nearest.coords,
        zoom: NEAREST_STOP_ZOOM,
        duration: 1200,
        essential: true,
      });
    },
    () => {
      // Permission denied or unavailable — leave the default framing in place.
      detach();
    },
    { enableHighAccuracy: false, timeout: 10_000, maximumAge: 60_000 },
  );
}

// nearestStop returns the closest Point feature in `fc` to (lon, lat), or
// null when the collection contains no usable points. Uses the equirectangular
// approximation, which is plenty accurate over the scale of a transit system.
function nearestStop(
  fc: GeoJSON.FeatureCollection,
  lon: number,
  lat: number,
): { coords: [number, number]; distanceKm: number } | null {
  let best: { coords: [number, number]; distanceKm: number } | null = null;
  for (const f of fc.features) {
    if (!f.geometry || f.geometry.type !== "Point") continue;
    const [slon, slat] = f.geometry.coordinates as [number, number];
    const d = haversineKm(lat, lon, slat, slon);
    if (!best || d < best.distanceKm) best = { coords: [slon, slat], distanceKm: d };
  }
  return best;
}

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}
